import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Mounts children only when scrolled near the viewport. SSR-safe:
 * renders a sized placeholder on the server and during the first client
 * paint, then hydrates real content as the user scrolls toward it.
 *
 * Use for below-the-fold sections to cut initial JS execution + DOM cost
 * without breaking SEO (the placeholder keeps stable layout, and crawlers
 * that execute JS will see the real content on scroll).
 */
export function LazyOnView({
  children,
  minHeight = "60vh",
  rootMargin = "600px 0px",
  className,
}: {
  children: ReactNode;
  minHeight?: string;
  rootMargin?: string;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (shown) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown, rootMargin]);

  return (
    <div ref={ref} className={className} style={shown ? undefined : { minHeight }}>
      {shown ? children : null}
    </div>
  );
}
